import { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import CommonWrapper from '../../components/ui_components/CommonComponentsStructureCss';
import { apiGetSellableCards, apiSellCard } from '../../api/cardApi';
import { apiGetMyCardAccount } from '../../api/userCardApi';
import type { SellableCard, CardRarity } from '../../types/models/card';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';
const toImgSrc = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const RARITY_COLOR: Record<CardRarity, string> = {
  common: 'rgba(255,255,255,0.55)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

const RARITY_LABEL: Record<CardRarity, string> = {
  common: 'Comune',
  rare: 'Rara',
  epic: 'Epica',
  legendary: 'Leggendaria',
};

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  padding: 40px 24px 80px;
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 36px;
  flex-wrap: wrap;
  gap: 16px;
`;

const HeaderLeft = styled.div``;

const Eyebrow = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(16, 185, 129, 0.7);
  margin-bottom: 8px;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 900;
  color: #10b981;
  margin: 0 0 8px;
`;

const PageSub = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

const CreditsBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 10px;
  padding: 12px 20px;
`;

const CreditsLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const CreditsValue = styled.span`
  font-size: 24px;
  font-weight: 900;
  color: #10b981;
`;

const EmptyText = styled.p`
  color: rgba(255, 255, 255, 0.35);
  text-align: center;
  padding: 80px 0;
  font-size: 15px;
`;

// ─── Grid ─────────────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 16px;
`;

const CardWrapper = styled.div`
  background: #111;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  flex-direction: column;
`;

const RarityBar = styled.div<{ $color: string }>`
  height: 3px;
  background: ${({ $color }) => $color};
`;

const CardImgArea = styled.div`
  aspect-ratio: 2 / 3;
  background: #0d0d0d;
  overflow: hidden;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const CardImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.1);
`;

const CardInfo = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const RarityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RarityDot = styled.div<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const RarityText = styled.span<{ $color: string }>`
  font-size: 11px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const WrestlerNames = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 16px;
`;

const QuantityInfo = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
`;

const BundleNote = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.25);
  font-style: italic;
`;

// ─── Quantity selector ────────────────────────────────────────────────────────

const QtyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  overflow: hidden;
  align-self: stretch;
`;

const QtyBtn = styled.button`
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.05);
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.1s;

  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.1); }
  &:disabled { opacity: 0.3; cursor: default; }
`;

const QtyValue = styled.span`
  flex: 1;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
`;

const ReceiveRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ReceiveLabel = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
`;

const ReceiveValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #10b981;
`;

const SellBtn = styled.button`
  width: 100%;
  padding: 9px;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.4);
  border-radius: 6px;
  color: #10b981;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  margin-top: auto;

  &:hover { background: rgba(16, 185, 129, 0.2); }
`;

// ─── Confirm modal ────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ModalBox = styled.div`
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 28px 32px;
  max-width: 420px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  margin: 0;
`;

const ModalBody = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
  line-height: 1.6;
`;

const ModalCredits = styled.div`
  font-size: 22px;
  font-weight: 900;
  color: #10b981;
  text-align: center;
  padding: 12px;
  background: rgba(16, 185, 129, 0.08);
  border-radius: 8px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  cursor: pointer;
`;

const ConfirmBtn = styled.button`
  flex: 2;
  padding: 10px;
  background: rgba(16, 185, 129, 0.15);
  border: 1px solid #10b981;
  border-radius: 8px;
  color: #10b981;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: default; }
`;

const ErrorMsg = styled.p`
  font-size: 12px;
  color: #f87171;
  margin: 0;
  text-align: center;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CediCartePage: React.FC = () => {
  const [cards, setCards] = useState<SellableCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [credits, setCredits] = useState<number | null>(null);
  const [confirmCard, setConfirmCard] = useState<SellableCard | null>(null);
  const [selling, setSelling] = useState(false);
  const [sellError, setSellError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([apiGetSellableCards(), apiGetMyCardAccount()])
      .then(([sellable, account]) => {
        setCards(sellable);
        setCredits(account.credits);
        const initQty: Record<number, number> = {};
        sellable.forEach((c) => { initQty[c.id] = 1; });
        setQuantities(initQty);
      })
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, []);

  const getQty = (card: SellableCard) => quantities[card.id] ?? 1;

  const changeQty = (card: SellableCard, delta: number) => {
    setQuantities((prev) => ({
      ...prev,
      [card.id]: Math.max(1, Math.min(card.sellable_quantity, (prev[card.id] ?? 1) + delta)),
    }));
  };

  const getCreditsPreview = (card: SellableCard) =>
    (quantities[card.id] ?? 1) * card.sell_price;

  const getApiQty = (card: SellableCard) => {
    const qty = quantities[card.id] ?? 1;
    return card.is_bundle ? qty * card.bundle_size : qty;
  };

  const openConfirm = (card: SellableCard) => {
    setSellError(null);
    setConfirmCard(card);
  };

  const handleConfirm = async () => {
    if (!confirmCard) return;
    setSelling(true);
    setSellError(null);
    try {
      const res = await apiSellCard(confirmCard.id, getApiQty(confirmCard));
      setCredits(res.credits_remaining);
      const soldQty = quantities[confirmCard.id] ?? 1;
      setCards((prev) =>
        prev
          .map((c) =>
            c.id === confirmCard.id
              ? { ...c, sellable_quantity: c.sellable_quantity - soldQty }
              : c
          )
          .filter((c) => c.sellable_quantity > 0)
      );
      setQuantities((prev) => ({ ...prev, [confirmCard.id]: 1 }));
      setConfirmCard(null);
    } catch (e: any) {
      setSellError(e?.message ?? 'Errore durante la cessione. Riprova.');
    } finally {
      setSelling(false);
    }
  };

  const describeSelection = (card: SellableCard) => {
    const qty = quantities[card.id] ?? 1;
    if (card.is_bundle) return `${qty} bundle (${qty * card.bundle_size} carte)`;
    return `${qty} ${qty === 1 ? 'carta' : 'carte'}`;
  };

  return (
    <CommonWrapper>
      <PageInner>
        <PageHeader>
          <HeaderLeft>
            <Eyebrow>La tua collezione</Eyebrow>
            <PageTitle>Cedi Carte</PageTitle>
            <PageSub>Cedi le copie in eccesso e ricevi crediti in cambio.</PageSub>
          </HeaderLeft>
          {credits !== null && (
            <CreditsBox>
              <CreditsLabel>Crediti</CreditsLabel>
              <CreditsValue>{credits}</CreditsValue>
            </CreditsBox>
          )}
        </PageHeader>

        {loading && <EmptyText>Caricamento...</EmptyText>}
        {!loading && cards.length === 0 && (
          <EmptyText>Nessuna carta disponibile per la cessione.</EmptyText>
        )}

        {!loading && cards.length > 0 && (
          <Grid>
            {cards.map((card) => {
              const src = toImgSrc(card.fronte_path);
              const rColor = RARITY_COLOR[card.rarity];
              const qty = getQty(card);
              const wrestlers = card.wrestlers.slice(0, 2).map((w) => w.name).join(', ');
              return (
                <CardWrapper key={card.id}>
                  <RarityBar $color={rColor} />
                  <CardImgArea>
                    {src
                      ? <CardImg src={src} alt={`Carta #${card.id}`} />
                      : <CardImgPlaceholder>#{card.id}</CardImgPlaceholder>
                    }
                  </CardImgArea>
                  <CardInfo>
                    <RarityRow>
                      <RarityDot $color={rColor} />
                      <RarityText $color={rColor}>{RARITY_LABEL[card.rarity]}</RarityText>
                    </RarityRow>
                    <WrestlerNames>{wrestlers || `#${card.id}`}</WrestlerNames>
                    <QuantityInfo>
                      Possiedi {card.owned_quantity} · Cedibili: {card.sellable_quantity}
                      {card.is_bundle ? ` bundle` : ''}
                    </QuantityInfo>
                    {card.is_bundle && (
                      <BundleNote>Si cedono a blocchi da {card.bundle_size}</BundleNote>
                    )}

                    <QtyRow>
                      <QtyBtn onClick={() => changeQty(card, -1)} disabled={qty <= 1}>−</QtyBtn>
                      <QtyValue>{qty}</QtyValue>
                      <QtyBtn onClick={() => changeQty(card, +1)} disabled={qty >= card.sellable_quantity}>+</QtyBtn>
                    </QtyRow>

                    <ReceiveRow>
                      <ReceiveLabel>Ricevi:</ReceiveLabel>
                      <ReceiveValue>+{getCreditsPreview(card)} cr.</ReceiveValue>
                    </ReceiveRow>

                    <SellBtn onClick={() => openConfirm(card)}>Cedi</SellBtn>
                  </CardInfo>
                </CardWrapper>
              );
            })}
          </Grid>
        )}
      </PageInner>

      {confirmCard && (
        <ModalOverlay onClick={(e) => e.target === e.currentTarget && !selling && setConfirmCard(null)}>
          <ModalBox>
            <ModalTitle>Conferma cessione</ModalTitle>
            <ModalBody>
              Stai cedendo <strong style={{ color: '#fff' }}>{describeSelection(confirmCard)}</strong>
              {confirmCard.wrestlers.length > 0 && (
                <> di <strong style={{ color: '#fff' }}>{confirmCard.wrestlers[0].name}</strong></>
              )}.
            </ModalBody>
            <ModalCredits>+{getCreditsPreview(confirmCard)} crediti</ModalCredits>
            {sellError && <ErrorMsg>{sellError}</ErrorMsg>}
            <ModalActions>
              <CancelBtn onClick={() => setConfirmCard(null)} disabled={selling}>Annulla</CancelBtn>
              <ConfirmBtn onClick={handleConfirm} disabled={selling}>
                {selling ? 'In corso...' : 'Conferma'}
              </ConfirmBtn>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </CommonWrapper>
  );
};

export default CediCartePage;
